// 📄 pages/Admin/components/TransactionFormModal.tsx
import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import {
  TransaccionSap,
  TransaccionSapData,
  CategoriaTransaccion,
  SolucionTransaccion,
  CATEGORIAS_TRANSACCION,
  SOLUCIONES_TRANSACCION,
  saveTransaccion
} from '../../../services/catalogoTransaccionesService.ts';

interface TransactionFormModalProps {
  transaccion: TransaccionSap | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const EMPTY_FORM: TransaccionSapData = {
  transaccion: '',
  nombre: '',
  categoria: 'abap',
  solucion: 'general',
  descripcion: ''
};

export default function TransactionFormModal({ transaccion, onClose, onSaved }: TransactionFormModalProps) {
  const [form, setForm] = useState<TransaccionSapData>(
    transaccion
      ? {
          transaccion: transaccion.transaccion,
          nombre: transaccion.nombre,
          categoria: transaccion.categoria,
          solucion: transaccion.solucion,
          descripcion: transaccion.descripcion
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof TransaccionSapData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.transaccion.trim()) {
      setError('El código de transacción es obligatorio.');
      return;
    }
    if (!form.nombre.trim()) {
      setError('El nombre/propósito de la transacción es obligatorio.');
      return;
    }

    setSaving(true);
    try {
      await saveTransaccion(transaccion?.id || generarId(), form, !transaccion);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">
            {transaccion ? 'Editar Transacción SAP' : 'Nueva Transacción SAP'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Transacción <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.transaccion}
                onChange={(e) => handleChange('transaccion', e.target.value)}
                placeholder="ej. SE18"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className={labelClass}>Nombre / Propósito <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                placeholder="ej. Definición y ampliación de BAdIs"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Categoría</label>
              <select
                className={inputClass}
                value={form.categoria}
                onChange={(e) => handleChange('categoria', e.target.value as CategoriaTransaccion)}
              >
                {CATEGORIAS_TRANSACCION.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Solución</label>
              <select
                className={inputClass}
                value={form.solucion}
                onChange={(e) => handleChange('solucion', e.target.value as SolucionTransaccion)}
              >
                {SOLUCIONES_TRANSACCION.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Para qué se usa esta transacción..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
